<template>
    <div ref="dayContainer" class="deliverySchedule__calendar">
        <div v-if="delivery && !isUndeliverableDay" class="deliverySchedule__calendarDay deliverySchedule__calendarDay--white"></div>
        <div v-else-if="isNoFlowerWeek || isUndeliverableDay" class="deliverySchedule__calendarDay background--paper z-10"></div>
        <div class="deliverySchedule__calendarDay deliverySchedule__calendarDay--white" :class="isToday ? 'deliverySchedule__calendarDay--bold' : null"/>
        <div class="deliverySchedule__calendarLabel">
            <div :class="calendarDayStyle"
                :style="circleStyleObject"
                @click="handleClickOnDeliveryDay(day)">
                <span v-text="deliveryDayLabel" v-if="!isUSA"></span>
                <span v-if="isUSA" :class="deliveryDayLabel === 'X' ? 'freddies-font' : ''">{{ deliveryDayLabel }}</span>
            </div>
        </div>

        <img v-if="isDeliveryDayLocked"
            src="~@/assets/images/decoration/lock-icon.png"
            class="deliverySchedule__calendarLock"
            @mouseover="showTooltip = !showTooltip"
            @mouseleave="showTooltip = !showTooltip">

        <transition name="transitionFade">
            <div v-if="showTooltip" class="deliverySchedule__toolTip">
                <p>{{ $t('CALENDAR_TOO_LATE_TO_MAKE_CHANGES') }}</p>
            </div>
        </transition>

        <span v-if="(isFirstDayOfMonth && !isNoFlowerWeek) || (isToday && !delivery)"
            class="deliverySchedule__calendarLabel--month"
            v-text="translatedMonth">
        </span>

        <div
            v-if="displayNoFlowersFlash"
            class="deliverySchedule__noFlowersLabel"
            :style="positionFlash">
            <p class="deliverySchedule__noFlowersText" v-html="noFlowersLabel"></p>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
    isObjectWithDateInArray,
    includesDay,
    includesDayInCurrentWeek,
    includesDayWithNoIdInCurrentWeek,
} from '@/helpers/DateHelpers';

export default {
    name: 'CalendarDay',
    props: {
        day: {
            required: true,
            type: Object,
        },
        startWeekDay: {
            required: true,
            type: Object,
        },
        deliveries: {
            required: true,
            type: Array,
        },
    },
    data() {
        return {
            isSelected: false,
            containerWidth: 0,
            showTooltip: false,
        };
    },
    mounted() {
        this.addToDeliveryDays();
        this.refreshFirstCutOffDay();
        this.isSelected = this.selectedDelivery && this.day.format('YYYY-MM-DD') === this.selectedDelivery.date;

        if (this.isFirstDayOfWeek) {
            this.$emit('update-holiday-week', this.isHolidayWeek);
        }
        this.containerWidth = this.$refs && this.$refs.dayContainer && this.$refs.dayContainer.clientWidth;
    },
    watch: {
        isDeliveryDay() {
            this.addToDeliveryDays();
        },
        selectedDelivery() {
            this.isSelected = this.selectedDelivery && this.day.format('YYYY-MM-DD') === this.selectedDelivery.date;
        },
        isCutOffDay() {
            this.refreshFirstCutOffDay();
        },
    },
    computed: {
        ...mapGetters({
            cutOffDayName: 'scheduledOrder/getCutOffDayName',
            deliveryDayName: 'scheduledOrder/getDeliveryDayName',
            holidays: 'scheduledOrder/getHolidays',
            optInDeliveryDays: 'scheduledOrder/getOptInDeliveryDays',
            selectedDelivery: 'scheduledOrder/getSelectedDelivery',
            today: 'scheduledOrder/getToday',
            undeliverableDays: 'scheduledOrder/getUndeliverableDays',
            deliverableDays: 'scheduledOrder/getDeliverableDays',
            isUSA: 'localisation/isUSA',
        }),
        delivery() {
            const filtered = this.deliveries.filter(delivery => delivery.datetime === this.day.format('YYYY-MM-DD'));
            const todaysDelivery = filtered.length ? filtered[0] : false;
            return todaysDelivery;
        },
        deliveryDayLabel() {
            return this.shouldShowX ? 'X' : this.day.format('D');
        },
        shouldShowX() {
            return this.isUSA && this.delivery && !this.delivery.delivery_scheduled;
        },
        circleStyleObject() {
            let styleObject = [];

            if (this.isSelected) {
                styleObject.push({ border: 'solid black 2px' });
            }
            if (this.isDayFromPast || this.isDeliveryDayLocked) {
                styleObject.push({ cursor: 'default', pointerEvents: 'none' });
            }
            if (this.shouldShowX) {
                styleObject.push({ fontWeight: 900 });
            }

            return styleObject;
        },
        calendarDayStyle() {
            const circleStyle = this.delivery && this.delivery.delivery_scheduled ? 'blue' : 'red';
            const baseCircleClass = 'deliverySchedule__calendarCircle';
            const calendarDayClass = this.delivery ? `${baseCircleClass} ${baseCircleClass}--${circleStyle}` : 'deliverySchedule__calendarDay';

            return calendarDayClass;
        },
        positionFlash() {
            const width = (this.containerWidth * 2) - 4;
            const left = this.containerWidth;
            const padding = (this.containerWidth / 3) - 4;
            const styleObject = {
                width: `${width}px`,
                left: `${left}px`,
                padding: `${padding}px 0 0 0`,
            };

            return styleObject;
        },
        cutOffDayMoment() {
            return this.$moment().day(this.cutOffDayName);
        },
        daysLeft() {
            const firstCutOff = this.$moment(this.firstCutOffDay);
            const cutOffWeekday = this.cutOffDayMoment.isoWeekday();
            const deliveryWeekday = this.day.isoWeekday();
            let cutOffDaysNumber = Math.abs(cutOffWeekday - deliveryWeekday);

            if (cutOffWeekday > deliveryWeekday) {
                cutOffDaysNumber = Math.abs(cutOffWeekday - 7 - deliveryWeekday);
            }
            return this.day.diff(firstCutOff, 'days') - this.todayMoment.diff(firstCutOff, 'days') - cutOffDaysNumber;
        },
        deliveryDays: {
            get() {
                return this.$store.getters['scheduledOrder/getDeliveryDays'];
            },
            set(value) {
                this.$store.commit('scheduledOrder/updateDeliveryDays', value);
            },
        },
        displayNoFlowersFlash() {
            return (this.isNoFlowerDay || this.isUndeliverableDay) &&
                !this.recentDelivery(-3) &&
                (!this.delivery && this.deliveries[0].delivery_scheduled);
        },
        firstCutOffDay: {
            get() {
                return this.$store.getters['scheduledOrder/getFirstCutOffDay'];
            },
            set(value) {
                this.$store.commit('scheduledOrder/updateFirstCutOffDay', value);
            },
        },
        isCutOffDay() {
            return this.day.format('dddd') === this.cutOffDayName;
        },
        isDayFromPast() {
            return this.day.isBefore(this.todayMoment);
        },
        isDeliveryDayLocked() {
            return this.delivery && this.delivery.cut_off_expired;
        },
        isFirstDayOfMonth() {
            return this.day.format('D') === '1';
        },
        isFirstDayOfWeek() {
            return this.day.isoWeekday() === 1;
        },
        isFirstWeek() {
            return this.day.clone()
                .startOf('isoWeek')
                .isSame(this.todayMoment.clone().startOf('isoWeek'));
        },
        isHoliday() {
            return includesDay(this.day, this.holidays);
        },
        isHolidayWeek() {
            return includesDayInCurrentWeek(this.day, this.holidays);
        },
        isLocked() {
            const firstWeekDayLocked = this.isFirstWeek && this.delivery && this.delivery.cut_off_expired;
            return (firstWeekDayLocked || this.isDayFromPast || this.daysLeft < 0);
        },
        isNoFlowerDay() {
            return this.isHoliday || (this.isOptInDelivery && !this.hasOptInOrder && !this.isUndeliverableDay);
        },
        isNoFlowerWeek() {
            return this.isHolidayWeek || this.isOptInDeliveryWeek || this.isUndeliverableWeek;
        },
        isOptInDelivery() {
            return includesDay(this.day, this.optInDeliveryDays);
        },
        isOptInDeliveryWeek() {
            return includesDayWithNoIdInCurrentWeek(this.day, this.optInDeliveryDays);
        },
        isToday() {
            return this.day.isSame(this.todayMoment, 'day');
        },
        isUndeliverableDay() {
            if (!this.delivery) {
                return false;
            }
            return includesDay(this.day, this.undeliverableDays);
        },
        isUndeliverableWeek() {
            return this.isUndeliverableDay || includesDayInCurrentWeek(this.day, this.undeliverableDays);
        },
        hasOptInOrder() {
            return this.optInDeliveryDays.filter(value => {
                return this.day.format('YYYY-MM-DD') === value.date && value.id !== null;
            }).length > 0;
        },
        noFlowersLabel() {
            return this.isUndeliverableDay ? this.$t('CALENDAR_WEEK_NO_FLOWERS') : this.$t('CALENDAR_WEEK_NO_FLOWERS_FACE');
        },
        todayMoment() {
            return this.$moment(this.today);
        },
        previousDelivery() {
            const index = this.deliverableDays.indexOf(this.day.format('YYYY-MM-DD')) - 1;

            if (index in this.deliverableDays) {
                return this.deliverableDays[index];
            }
        },
        nextDelivery() {
            const index = this.deliverableDays.indexOf(this.day.format('YYYY-MM-DD')) + 1;

            if (index in this.deliverableDays) {
                return this.deliverableDays[index];
            }
        },
        translatedMonth() {
            return this.day.locale(process.env.VUE_APP_MOMENT_LOCALE || 'de').format('MMM');
        },
    },
    methods: {
        recentDelivery(num) {
            const today = this.$moment().startOf('day');
            if (this.delivery) {
                const deliveryDate = this.$moment(this.delivery.datetime);
                const diff = deliveryDate.diff(today, 'days');
                return diff > num && diff < 0;
            }
            return false;
        },
        addToDeliveryDays() {
            const isDeliveryDay = this.delivery && !this.isDayFromPast && !this.isDeliveryDayLocked;

            if (isDeliveryDay && !isObjectWithDateInArray(this.day.format('YYYY-MM-DD'), this.deliveryDays)) {
                this.deliveryDays.push(this.createDeliveryDay());
            }
        },
        createDeliveryDay() {
            return {
                day: {
                    name: this.day.format('ddd'),
                    day: this.day.format('D'),
                    month: this.day.format('MMM'),
                    previous: this.previousDelivery,
                    next: this.nextDelivery,
                },
                date: this.day.format('YYYY-MM-DD'),
                isLocked: this.isDeliveryDayLocked,
                daysLeft: this.daysLeft,
                isHoliday: this.isHoliday,
                startWeekDay: this.startWeekDay.format('YYYY-MM-DD'),
                isSelected: false,
                undeliverable: this.isUndeliverableDay,
            };
        },
        handleClickOnDeliveryDay(selectedDay) {
            this.$store.dispatch('scheduledOrder/selectDeliveryDay', selectedDay.format('YYYY-MM-DD'));
        },
        refreshFirstCutOffDay() {
            if (this.isCutOffDay && this.firstCutOffDay == null && typeof this.day.format('YYYY-MM-DD') === 'string') {
                this.firstCutOffDay = this.day.format('YYYY-MM-DD');
            }
        },
    },
};
</script>

<style scoped>
.freddies-font {
    font-family: FreddiesFlowersTitle;
}
</style>
